import React, { useState, useEffect } from "react"
import { useIntl, FormattedMessage } from "react-intl"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import InputGroup from "react-bootstrap/InputGroup"
import Container from "react-bootstrap/Container"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"
import { Search, PencilSquare, Trash, PlusCircle, Backspace, People } from "react-bootstrap-icons" // Iconos de Bootstrap

import { AlertError, AlertLoading } from '../helpers/AlertHelper'

import FormModal from "../components/modals/FormModal"
import TableManagement from "../components/tables/TableManagement"

import CustomersService from "../services/CustomersService"

import CustomersForm from "./CustomersForm"

const Customers = () => {
    const intl = useIntl()
    const [customerId, setCustomerId] = useState(null)
    const [customers, setCustomers] = useState([])
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(null)
    const [currPage, setCurrPage] = useState(1)
    const [search, setSearch] = useState("")
    const [sortBy, setSortBy] = useState("name")
    const [sortOrder, setSortOrder] = useState("asc")
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(20)

    // Setting modal title
    const [modalTitle, setModalTitle] = useState(false)

    // Setting modal icon
    const [modalIcon, setModalIcon] = useState(false)

    // Open modal form control to add/edit element
    const [modalTrigger, setModalTrigger] = useState(false)

    // Setting modal action enum[add,edit,delete]
    const [modalAction, setModalAction] = useState(false)

    // Fetch data
    const fetchCustomers = async () => {
        try {
            const response = await CustomersService.getCustomers(currPage, search, perPage, sortBy, sortOrder)
            setCustomers(response.data.customers)
            setTotalRows(response.data.pagination.totalItems)
            setIsLoaded(true)
        } catch (err) {
            setIsLoaded(false)
            setError(err)
        }
    }

    const handleModal = () => {
        setModalTrigger(false)
        fetchCustomers()
    }

    const handleSearchChange = (e) => {
        setSearch(e.target.value)
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    const handleSearchClean = () => {
        setSearch('')
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    // Re-fetch data on page/perPage change
    useEffect(() => {
        fetchCustomers()
    }, [currPage, perPage, search, sortBy, sortOrder])

    const handleButtonClick = ({ data, type }) => {
        // Definir un objeto para mapear tipos a títulos e íconos
        const modalConfig = {
            add: {
                title: <FormattedMessage id="customersAdd" defaultMessage="Add customer" />,
                icon: <PlusCircle />,
            },
            edit: {
                title: <FormattedMessage id="customersEdit" defaultMessage="Edit customer" />,
                icon: <PencilSquare />,
            },
            delete: {
                title: <FormattedMessage id="customersDelete" defaultMessage="Delete customer" />,
                icon: <Trash />,
            }
        }

        // Establecer la configuración del modal dependiendo del tipo
        const config = modalConfig[type] || {}

        if (config) {
            setCustomerId(type === 'add' ? false : data._id) // Si 'add', customerid será falso
            setModalTitle(config.title) // Set title from the config
            setModalIcon(config.icon) // Set icon from the config
            setModalAction(type)
            setModalTrigger(true)
        }
    }

    const handleRowsPerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage)
        setCurrPage(page)
    }

    const handleSort = async (column, sortDirection) => {
        setSortOrder(sortDirection)
        setSortBy(column.id)
        setCurrPage(1)
    }

    const columns = [
        {
            id: 'name',
            name: <FormattedMessage id="customersName" defaultMessage="Name" />,
            selector: (row) => row.name,
            sortable: true,
        },
        {
            id: 'vat',
            name: <FormattedMessage id="customersVAT" defaultMessage="VAT number" />,
            selector: (row) => row.vat,
            sortable: true,
        },
        {
            id: 'email',
            name: <FormattedMessage id="customersEmail" defaultMessage="E-mail" />,
            selector: (row) => row.email,
            sortable: true,
        },
        {
            id: 'phone',
            name: <FormattedMessage id="customersPhoneNumber" defaultMessage="Phone" />,
            selector: (row) => row.phone,
        }
    ]


    return (
        <Container fluid className="p-0">
            <Row className="p-1 bg-secondary">
                <Col>
                    <InputGroup>
                        <InputGroup.Text><Search /></InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder={intl.formatMessage({ id: "textSearch", defaultMessage: "Search customers..." })}
                            value={search}
                            onChange={handleSearchChange}
                        />
                        <Button onClick={handleSearchClean} variant="light">
                            <Backspace /> <FormattedMessage id="textClean" defaultMessage="Clean" />
                        </Button>

                        <Button onClick={() => handleButtonClick({ data: null, type: 'add' })} variant="primary">
                            <PlusCircle /> <FormattedMessage id="textAdd" defaultMessage="Add" />
                        </Button>
                    </InputGroup>
                </Col>
            </Row>
            {isLoaded ? (
                <TableManagement
                    icon={<People />}
                    title={<FormattedMessage id="navCustomers" defaultMessage="Customers" />}
                    columns={columns}
                    data={customers}
                    paginationPerPage={perPage}
                    paginationTotalRows={totalRows}
                    onChangePage={setCurrPage}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onSort={handleSort}
                    onActionClick={handleButtonClick}
                />
            ) : error ? (
                <AlertError error={error} />
            ) : (
                <AlertLoading isLoaded={isLoaded} />
            )}

            <FormModal
                title={modalTitle}
                icon={modalIcon}
                body={CustomersForm}
                refreshData={handleModal}
                modalTrigger={modalTrigger}
                elementId={customerId}
                action={modalAction} />

        </Container>
    )
}

export default Customers